// Persist-first upload queue for inspection frames (R114 rewrite).
//
// Field report (Stacy, 2026-10-11): "8 'uploading' where the red arrow is, then
// that note disappears… the number of 'saved offline' goes up… photos from past
// months, many closed inspections ago." The old queue saved a frame to
// IndexedDB only AFTER its upload failed, replayed oldest-first, and STOPPED at
// the first failure forever — and its oldest rows named inspections that no
// longer exist (404 every time), so nothing behind them ever moved. It also had
// no automatic sync on steady WiFi and no idempotency key (a retry after "it
// uploaded but the reply never came back" made a duplicate).
//
// Now every frame goes through here, from the moment it is captured:
//   • PERSIST FIRST: the raw file goes into IndexedDB before any network call
//     (iOS can suspend/kill the page mid-upload — the photo is already safe;
//     capture="environment" shots never reach the camera roll). It is shrunk
//     only when SENT (falling back to the raw file), so the stored copy is the
//     original until the server confirms it has the photo.
//   • the SAME DB ('surge-capture', store 'frames', version 1). No upgrade, no
//     cursor.update: a ~350 MB blob rewrite on iOS can hit quota and leave the
//     queue unreadable. Rows queued by the old client have no key — theirs is
//     derived lazily (lg_<id>_<queuedAt base36>, lib/frameQueuePolicy.mjs) and
//     the server dedupes them by byte size against storage. attempts / retryAt
//     / 'attention' live in memory (like photoUploader), so an attention row is
//     tried once more per session — never deleted.
//   • a pump with NO head-of-line blocking: one module-level pool (mutex,
//     concurrency 3), a per-request AbortController timeout (30 s + 15 s/MB,
//     ≤ 120 s). 2xx from our server (attached / deduped / held) → delete the
//     local copy. 401/403/408/429/5xx/network/timeout → backoff and rotate,
//     the loop never stops. 400/404/413/415/422 → 'attention', kept forever for
//     a person (Save to phone / Discard in the queue sheet). Unreadable blobs
//     (an iOS WebKit failure mode) → 'attention: unreadable', never a retry loop.
//   • rows with NO inspection id (the old client queued them after Save & exit)
//     go straight to the office hold route, never to /inspections/null/photo.
//   • wake triggers: boot + 2.5 s, online, pageshow, visible, a 30 s interval
//     while anything is pending, and Sync now.
//
// Why a second pump instead of photoUploader's retain-on-reject: the frame
// rows live in a different DB with legacy rows that must never be rewritten,
// need per-inspection counts (incl. null ids), 'attention' rows kept forever,
// a hold route for id-less rows, the legacy key + orig_size, and a per-request
// timeout. Folding all of that into photoUploader would put a mode flag on
// every branch of the cleaners' turn-photo uploader, which works; the shared
// pieces (shrinkForUpload, the outcome / backoff / timeout rules in
// lib/frameQueuePolicy.mjs) are shared instead.
//
// Events: window 'surge-frame-queue' {type:'change'} on any state change and
// {type:'uploaded', inspectionId, clientKey, area, url, inspection, placement,
// late} when the server confirmed a photo. Pages read state synchronously via
// snapshot() / counts() during render.

import {
  ATTENTION, classifyOutcome, backoffMs, uploadTimeoutMs, legacyKey, splitCounts, inspectionKeyOf,
  type SplitCounts,
} from '../../../lib/frameQueuePolicy.mjs';
import { shrinkForUpload } from './photoUploader';

const DB_NAME = 'surge-capture';
const STORE = 'frames';
const VERSION = 1;
const CONCURRENCY = 3;
const API = '/api/assetops/inspections';

/** A row as stored in IndexedDB. Legacy rows (old client) have only the first six fields. */
export interface QueuedFrame {
  id?: number;
  inspectionId: string | null;
  area: string;
  name: string;
  blob: Blob;
  queuedAt: number;            // when the photo was taken (sent as taken_at)
  clientKey?: string;          // R114: minted at capture (fk_…)
  listingId?: string | null;   // R114: helps the server when the inspection is gone
  v?: number;
}

/** The in-memory view of a row. */
export interface FrameItem extends QueuedFrame {
  key: string;                 // fk_… or the derived lg_… key
  size: number;
  attempts: number;
  retryAt?: number;
  state?: 'attention';
  note?: string;               // why it needs attention
  lastStatus?: number;
  memOnly?: boolean;           // IndexedDB refused it — lives only in this tab
}

export interface SyncSummary { uploaded: number; held: number; attention: number; remaining: number }

function available(): boolean { return typeof indexedDB !== 'undefined'; }

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      // first use only (VERSION stays 1 — existing rows are never touched)
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        os.createIndex('inspectionId', 'inspectionId', { unique: false });
      }
    };
    req.onsuccess = () => { const db = req.result; db.onversionchange = () => db.close(); resolve(db); };
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('capture storage is blocked by another open copy of the app'));
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T> {
  return openDb().then(db => new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error);
    t.oncomplete = () => db.close();
    t.onabort = () => { db.close(); reject(t.error || req.error); };
  }));
}

// ── in-memory mirror (IndexedDB stays the source of truth) ─────────────────
let mem: FrameItem[] = [];
let idbDead = false;
let loaded = false;
let loading: Promise<void> | null = null;
let persistAsked = false;

function toItem(r: QueuedFrame): FrameItem {
  const key = r.clientKey || legacyKey(r.id, r.queuedAt) || `lg_x_${Math.random().toString(36).slice(2, 10)}`;
  const size = r.blob && typeof r.blob.size === 'number' ? r.blob.size : 0;
  return { ...r, key, size, attempts: 0 };
}

export function loadQueue(): Promise<void> {
  if (loaded) return Promise.resolve();
  if (!loading) {
    loading = (async () => {
      if (available()) {
        try {
          const rows = await tx<QueuedFrame[]>('readonly', s => s.getAll());
          const fresh = mem.filter(m => m.id == null);   // memory-only rows enqueued while loading
          const known = new Set(mem.filter(m => m.id != null).map(m => m.id));
          mem = [...mem.filter(m => m.id != null), ...(rows || []).filter(r => !known.has(r.id)).map(toItem), ...fresh];
        } catch { idbDead = true; }
      } else idbDead = true;
      loaded = true;
      emit();
    })();
  }
  return loading;
}

function emit(detail: any = { type: 'change' }): void {
  try { window.dispatchEvent(new CustomEvent('surge-frame-queue', { detail })); } catch { /* non-browser */ }
}

function newKey(): string {
  return 'fk_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}
// a camera-roll pick keeps its own taken time; a live shot / video frame is now
function takenOf(file: Blob): number {
  const lm = file instanceof File ? file.lastModified : NaN;
  return Number.isFinite(lm) && lm > Date.parse('2024-01-01') && lm <= Date.now() + 5 * 60000 ? lm : Date.now();
}

/**
 * Persist one captured frame (raw) and start uploading it. Resolves once the
 * photo is safe on the phone — IndexedDB, or memory when this browser refuses
 * storage (then storageWarning() is set and the page warns loudly).
 */
export async function enqueueFrame(o: { inspectionId: string | null; listingId?: string | null; area: string; file: Blob; name?: string }): Promise<FrameItem> {
  await loadQueue();
  const name = o.name || (o.file instanceof File && o.file.name) || `${String(o.area || 'frame').replace(/\s+/g, '_')}.jpg`;
  const row: QueuedFrame = {
    inspectionId: o.inspectionId || null,
    listingId: o.listingId != null ? String(o.listingId) : null,
    area: o.area || 'General',
    name,
    blob: o.file,
    queuedAt: takenOf(o.file),
    clientKey: newKey(),
    v: 2,
  };
  let item: FrameItem;
  if (available() && !idbDead) {
    try {
      const id = await tx<IDBValidKey>('readwrite', s => s.add(row));
      item = toItem({ ...row, id: id as number });
    } catch {
      item = { ...toItem(row), memOnly: true };
    }
  } else item = { ...toItem(row), memOnly: true };
  if (!persistAsked) {
    persistAsked = true;
    try { void (navigator as any).storage?.persist?.(); } catch { /* best-effort */ }
  }
  mem.push(item);
  emit();
  kick({ priority: item.inspectionId || null });   // the photo just taken goes first
  return item;
}

// ── reads for rendering (synchronous) ─────────────────────────────────────
export function snapshot(): FrameItem[] { return mem.slice(); }
export function counts(activeId: string | null | undefined): SplitCounts { return splitCounts(mem, activeId); }
export function isLoaded(): boolean { return loaded; }
export function isSyncing(): boolean { return pumping; }
export function storageWarning(): boolean { return idbDead || mem.some(m => m.memOnly); }
/** This inspection's photos still on the phone, per room tag (not rejected). */
export function pendingByArea(inspectionId: string | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  if (!inspectionId) return out;
  for (const m of mem) {
    if (inspectionKeyOf(m) !== String(inspectionId) || m.state === ATTENTION) continue;
    out[m.area || 'General'] = (out[m.area || 'General'] || 0) + 1;
  }
  return out;
}
export function inFlightCount(): number { return inFlight.size; }

async function removeItem(item: FrameItem): Promise<void> {
  mem = mem.filter(m => m !== item);
  if (item.id != null && available()) { try { await tx('readwrite', s => s.delete(item.id!)); } catch { /* the memory copy is already gone */ } }
}

/** Discard photos (the queue sheet, after an explicit confirm). By key. */
export async function discard(keys: string[]): Promise<number> {
  const set = new Set(keys);
  const gone = mem.filter(m => set.has(m.key) && !inFlight.has(m));
  for (const g of gone) await removeItem(g);
  emit();
  return gone.length;
}

// ── the pump ──────────────────────────────────────────────────────────────
let pumping = false;
let repump = false;
let timer: number | null = null;
const inFlight = new Set<FrameItem>();
let priority: string | null = null;
let idleWaiters: Array<() => void> = [];
const tally = { uploaded: 0, held: 0, attention: 0 };

/** Wake the pump. clear: forget backoff stamps (a real wake signal / Sync now). */
export function kick(opts: { clear?: boolean; priority?: string | null } = {}): void {
  if (opts.priority !== undefined) priority = opts.priority;
  if (opts.clear) for (const m of mem) delete m.retryAt;
  if (timer) { clearTimeout(timer); timer = null; }
  void pump();
}

/** Upload one inspection's photos first (the queue sheet's per-row Upload); retries its rejected ones once. */
export function prioritize(inspectionId: string | null, retryRejected = false): void {
  for (const m of mem) {
    if (inspectionKeyOf(m) !== (inspectionId == null ? null : String(inspectionId))) continue;
    delete m.retryAt;
    if (retryRejected && m.state === ATTENTION && m.note !== 'unreadable') { delete m.state; delete m.note; }
  }
  kick({ priority: inspectionId == null ? '__none__' : String(inspectionId) });
}

/** Sync now: clear backoff, run until idle, report what moved. */
export async function syncNow(): Promise<SyncSummary> {
  const before = { ...tally };
  await loadQueue();
  const idle = new Promise<void>(r => idleWaiters.push(r));
  kick({ clear: true });
  await idle;
  return {
    uploaded: tally.uploaded - before.uploaded,
    held: tally.held - before.held,
    attention: tally.attention - before.attention,
    remaining: mem.length,
  };
}

// a transient failure goes to the BACK of the line, so one bad photo (or one
// dead inspection) can never starve the photos behind it
function rotate(rec: FrameItem): void {
  const i = mem.indexOf(rec);
  if (i >= 0) { mem.splice(i, 1); mem.push(rec); }
}
function eligible(m: FrameItem, now: number): boolean {
  return !inFlight.has(m) && m.state !== ATTENTION && (!m.retryAt || m.retryAt <= now);
}
function pickNext(): FrameItem | null {
  const now = Date.now();
  if (priority) {
    const p = mem.find(m => eligible(m, now) && (priority === '__none__' ? inspectionKeyOf(m) == null : inspectionKeyOf(m) === priority));
    if (p) return p;
  }
  return mem.find(m => eligible(m, now)) || null;
}

function scheduleNext(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  const waits = mem.filter(m => m.state !== ATTENTION && m.retryAt).map(m => m.retryAt! - Date.now());
  if (!waits.length) return;
  const ms = Math.max(2000, Math.min(...waits));
  timer = window.setTimeout(() => { timer = null; void pump(); }, ms);
}

async function pump(): Promise<void> {
  if (pumping) { repump = true; return; }
  pumping = true;
  emit();
  try {
    await loadQueue();
    const worker = async (): Promise<void> => {
      for (;;) {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        const rec = pickNext();
        if (!rec) return;
        inFlight.add(rec);
        emit();
        let out: string;
        try { out = await sendOne(rec); } catch { out = 'retry'; rec.attempts++; rec.retryAt = Date.now() + backoffMs(rec.attempts); rotate(rec); }
        finally { inFlight.delete(rec); }
        emit();
        // a transient failure retires THIS worker (the others finish their
        // items; the timer resumes the rest) — the queue itself never stops
        if (out === 'retry') return;
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));
  } finally {
    pumping = false;
    scheduleNext();
    if (repump) { repump = false; void pump(); }
    else { const w = idleWaiters; idleWaiters = []; w.forEach(f => f()); }
    emit();
  }
}

async function readable(b: Blob): Promise<boolean> {
  try { await b.slice(0, 64).arrayBuffer(); return true; } catch { return false; }
}

async function sendOne(rec: FrameItem): Promise<'done' | 'retry' | 'attention'> {
  if (!rec.blob || !(rec.blob.size > 0) || !(await readable(rec.blob))) {
    rec.state = ATTENTION; rec.note = 'unreadable'; rec.lastStatus = 0;
    tally.attention++;
    return 'attention';
  }
  // shrink on SEND (the raw original stays in IndexedDB until the server confirms)
  let body: Blob = rec.blob;
  let name = rec.name || 'frame.jpg';
  if (!/^video\//i.test(rec.blob.type || '')) {
    try {
      const f = rec.blob instanceof File ? rec.blob : new File([rec.blob], name, { type: rec.blob.type || 'image/jpeg', lastModified: rec.queuedAt || Date.now() });
      const s = await shrinkForUpload(f);
      if (s.blob && s.blob.size > 0) { body = s.blob; name = s.name; }
    } catch { /* send the raw file */ }
  }
  const fd = new FormData();
  fd.append('bucket', 'frame');
  fd.append('client_key', rec.key);
  if (rec.area) fd.append('area', rec.area);
  if (rec.queuedAt) fd.append('taken_at', String(Math.round(rec.queuedAt)));
  if (rec.attempts) fd.append('attempt', String(rec.attempts));
  if (rec.listingId) fd.append('listing_id', String(rec.listingId));
  if (!rec.clientKey) fd.append('orig_size', String(rec.blob.size));   // legacy: the server matches the bytes the old client may already have landed
  fd.append('photo', body, name);
  const insp = inspectionKeyOf(rec);
  const url = insp ? `${API}/${encodeURIComponent(insp)}/photo` : `${API}/orphan-frames/hold`;
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const to = ctl ? window.setTimeout(() => ctl.abort(), uploadTimeoutMs(body.size)) : 0;
  let status = 0;
  let json: any = null;
  try {
    const r = await fetch(url, { method: 'POST', body: fd, ...(ctl ? { signal: ctl.signal } : {}) });
    status = r.status;
    json = await r.json().catch(() => null);
  } catch { status = 0; }
  finally { if (to) clearTimeout(to); }
  const out = classifyOutcome(status, json);
  rec.lastStatus = status;
  if (out === 'done') {
    await removeItem(rec);
    if (json && json.placement === 'held') tally.held++; else tally.uploaded++;
    emit({
      type: 'uploaded', inspectionId: insp, clientKey: rec.key, area: rec.area,
      url: (json && ((json.urls && json.urls[0]) || json.url)) || null,
      inspection: (json && json.inspection) || null,
      placement: (json && json.placement) || 'attached',
      late: !!(json && json.late), deduped: !!(json && json.deduped),
    });
  } else if (out === 'attention') {
    rec.state = ATTENTION;
    rec.note = (json && json.error) ? String(json.error).slice(0, 140) : `HTTP ${status}`;
    tally.attention++;
    console.warn('[captureQueue] kept for review', status, rec.key, rec.note);
  } else {
    rec.attempts++;
    rec.retryAt = Date.now() + backoffMs(rec.attempts);
    rotate(rec);
  }
  return out;
}

// ── wake-up triggers ──────────────────────────────────────────────────────
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const wake = () => { if (mem.length || !loaded) kick({ clear: true }); };
  window.addEventListener('online', wake);
  window.addEventListener('pageshow', wake);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') wake(); });
  window.setTimeout(() => { void loadQueue().then(() => { if (mem.length) kick(); }); }, 2500);
  // steady WiFi fires no 'online' — check every 30 s while anything is waiting
  window.setInterval(() => {
    if (document.visibilityState !== 'visible' || pumping) return;
    if (mem.some(m => m.state !== ATTENTION && (!m.retryAt || m.retryAt <= Date.now()))) void pump();
  }, 30000);
}
